import { useEffect, useState } from "react";

import type { SldWarning, StructuralError } from "../services/sld";
import type { SldDraft } from "../sld/sldDraft";
import { previewModelFromDraft, SldDiagram } from "./SldDiagram";

// The live picture under the single-line diagram editor (SLD-a, A26 (g)). It owns the pause
// and the model it draws, so a debounce tick re-renders this component and the picture
// only, never the form's fieldsets. A tick whose model reads the same as the last one keeps
// the previous model object, so the memoised picture and its layout are not built again: a
// keystroke in the notes, or one that leaves the model as it was, costs nothing.

/** How long the picture waits after the last change of the draft before it is drawn again. */
export const PREVIEW_DELAY_MS = 200;

type SldPreviewProps = {
  draft: SldDraft;
  /** Structural errors of the last refused save, marked on the picture. */
  errors?: readonly StructuralError[] | undefined;
  /** Warnings of the stored document, marked on the picture. */
  warnings?: readonly SldWarning[] | undefined;
};

export function SldPreview({ draft, errors, warnings }: SldPreviewProps) {
  const [model, setModel] = useState(() => previewModelFromDraft(draft));

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = previewModelFromDraft(draft);
      setModel((previous) =>
        JSON.stringify(previous) === JSON.stringify(next) ? previous : next,
      );
    }, PREVIEW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [draft]);

  return <SldDiagram model={model} errors={errors} warnings={warnings} />;
}

export default SldPreview;
